import React, { useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { normalizeWidgetCode, NormalizedWidgetCode, withBrandFixes, withMovedImages } from 'shared/lib/widgetCode/normalizeWidgetCode';
import type { BrandFixKind } from 'shared/lib/widgetCode/brandCheck';
import useHouseStyle from '~/lib/useHouseStyle';
import PasteBox from '~/pages/components/PageEditor/aiWidget/PasteBox';
import PasteReport from '~/pages/components/PageEditor/aiWidget/PasteReport';
import { T } from '~/pages/components/PageEditor/aiWidget/tokens';
import useMoveImages, { describeFailures } from '~/pages/components/PageEditor/aiWidget/useMoveImages';
import { applyMovedImages } from '~/lib/moveImagesToAssets';
import PasteHistory, { useSavePaste } from '~/pages/components/PageEditor/aiWidget/PasteHistory';
import LivePreview from './LivePreview';
import { S, A, DARK } from '~/app/ui/tokens';

type Props = {
  widgetName: string;
  props: Record<string, unknown>;
  hasCode: boolean;
  onKeep: (code: Pick<NormalizedWidgetCode, 'html' | 'css' | 'js' | 'scoper'>) => void;
  onClose: () => void;
};

const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(23, 26, 32, 0.45)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '48px 16px',
    overflowY: 'auto',
    zIndex: 1000,
    [DARK]: { background: S.scrim }
  },
  modal: {
    width: 'min(980px, 100%)',
    background: A.surface,
    borderRadius: 16,
    border: `1px solid ${T.line}`,
    boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
    padding: '24px clamp(16px, 3vw, 28px)',
    display: 'grid',
    gap: 18,
    color: T.fg,
    [DARK]: { boxShadow: A.pop }
  },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 700 },
  close: { all: 'unset', cursor: 'pointer', fontSize: 22, lineHeight: 1, color: T.muted, padding: 4 },
  lead: { margin: 0, fontSize: 13, color: T.muted, lineHeight: 1.5 },
  proposal: {
    borderRadius: 12,
    border: `1px solid ${T.clay}`,
    padding: 16,
    display: 'grid',
    gap: 14,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '0 0 auto 0',
      height: 4,
      background: `repeating-linear-gradient(135deg, ${T.clay} 0 8px, transparent 8px 16px)`
    }
  },
  proposalTitle: { margin: 0, fontSize: 14, fontWeight: 700, color: T.clayInk, display: 'flex', alignItems: 'center', gap: 8 },
  dot: { width: 8, height: 8, borderRadius: '50%', background: T.clay, display: 'inline-block' },
  frame: { height: 340, border: `1px solid ${T.line}`, borderRadius: 8, overflow: 'hidden' },
  actions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  keep: { padding: '9px 18px', fontSize: 13, fontWeight: 600, borderRadius: 980, border: `1px solid ${T.fg}`, background: T.fg, color: S.onFg, cursor: 'pointer', '&[disabled]': { opacity: 0.5, cursor: 'default' } },
  quiet: { padding: '9px 18px', fontSize: 13, fontWeight: 600, borderRadius: 980, border: `1px solid ${T.fieldLine}`, background: A.surface, color: T.fg, cursor: 'pointer', '&[disabled]': { opacity: 0.5, cursor: 'default' } },
  note: { fontSize: 12, color: T.muted },
  error: { fontSize: 13, color: T.red }
});

/** Paste a reply from any AI tool into the custom widget builder, review it, then keep it. */
const PasteCodeModal: React.FC<Props> = ({ widgetName, props, hasCode, onKeep, onClose }) => {
  const c = useStyles();
  const [proposal, setProposal] = useState<NormalizedWidgetCode | null>(null);
  // Bumped after a proposal is discarded, so the paste box empties.
  const [pasteReset, setPasteReset] = useState(0);
  const [raw, setRaw] = useState('');
  const rawRef = useRef('');
  rawRef.current = raw;
  const target = `custom-widget-${widgetName}`.replace(/[^\w-]/g, '-').slice(0, 120);
  const savePaste = useSavePaste(target);
  const houseStyle = useHouseStyle();
  const check = (text: string) => {
    setRaw(text);
    setProposal(normalizeWidgetCode(text, `custom-widget-root-${widgetName}`, houseStyle));
  };
  const brand = (kind: BrandFixKind) => ({ label: 'Use house style', onClick: () => { if (proposal) setProposal(withBrandFixes(proposal, kind, houseStyle)); } });
  const { move, busy } = useMoveImages();
  const [imageError, setImageError] = useState<string | null>(null);
  const folder = `custom-widget-${widgetName}`.replace(/[^\w-]/g, '-').slice(0, 80);
  const moveImages = async () => {
    if (!proposal) return;
    setImageError(null);
    const started = raw;
    const result = await move({ html: proposal.html, css: proposal.css }, proposal.images, folder);
    // Applied to the proposal as it is now (a house style fix may have changed it), if it's still this paste.
    setProposal(current => (current && rawRef.current === started
      ? withMovedImages(current, applyMovedImages(current, result.moved), result.moved.length, `custom-widget-root-${widgetName}`)
      : current));
    setImageError(describeFailures(result.failed));
  };
  const imageAction = { label: busy ? 'Moving…' : 'Move to Assets', onClick: moveImages, busy };

  return (
    <div className={c.overlay} onClick={onClose} role="presentation">
      <div className={c.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Paste from your AI tool" data-testid="paste-modal">
        <div className={c.head}>
          <h2 className={c.title}>Paste from your AI tool</h2>
          <button type="button" className={c.close} onClick={onClose} aria-label="Close">×</button>
        </div>
        <p className={c.lead}>
          Genesis pulls out the code, fixes what would break on ff.com and shows you a preview first. Keeping it replaces the code in the draft.
        </p>
        <PasteBox briefTarget="custom-widget" onCheck={check} onClear={() => setProposal(null)} resetKey={pasteReset} houseStyle={houseStyle} />
        <PasteHistory target={target} onLoad={check} />
        {proposal && (
          <section className={c.proposal} data-testid="proposal">
            <h3 className={c.proposalTitle}><span className={c.dot} />Proposal · not kept yet{proposal.js.trim() ? ' · its script runs once you keep it' : ''}</h3>
            <div className={c.frame}>
              <LivePreview html={proposal.html} css={proposal.css} js={proposal.js} props={props} widgetName={widgetName} scoper={proposal.scoper} runScripts={false} />
            </div>
            <PasteReport
              fixes={proposal.fixes}
              findings={proposal.findings}
              fixRequest={proposal.fixRequest}
              actions={{ 'base64-images': imageAction, 'outside-images': imageAction, 'brand-colors': brand('colors'), 'brand-fonts': brand('fonts'), 'brand-radii': brand('radii') }}
            />
            {imageError && <div className={c.error}>{imageError}</div>}
            <div className={c.actions}>
              <button type="button" className={c.keep} onClick={() => { savePaste(raw); onKeep(proposal); }} disabled={busy} title={busy ? 'Wait for the images to finish moving' : undefined}>Keep this code</button>
              <button type="button" className={c.quiet} onClick={() => { setProposal(null); setPasteReset(n => n + 1); }} disabled={busy}>Discard</button>
              {hasCode && <span className={c.note}>Keeping replaces the code in the draft.</span>}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PasteCodeModal;
